import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardTitle,
  Form,
  Button,
  Alert,
  Spinner,
  Row,
  Col
} from 'reactstrap';
import axios from '../../api/Axios';
import { CURRENCIES, DEFAULT_CURRENCY } from '../../constants/currencies';
import '../../styles/FormValidation.css';

const CreateFunder = () => {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    country: '',
    name: '',
    short_name: '',
    funder_code: '',
    domain: '',
    default_currency: DEFAULT_CURRENCY
  });

  // Representative form state
  const [primaryRepData, setPrimaryRepData] = useState({
    first_name: '',
    last_name: '',
    title: '',
    phone_number: '',
    email_address: ''
  });

  const [secondaryRepData, setSecondaryRepData] = useState({
    first_name: '',
    last_name: '',
    title: '',
    phone_number: '',
    email_address: ''
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [countries, setCountries] = useState([]);
  const [creationStep, setCreationStep] = useState('form'); // 'form', 'complete'
  
  // Validation state
  const [validation, setValidation] = useState({
    name: { isValid: null, message: '' },
    domain: { isValid: null, message: '' },
    short_name: { isValid: null, message: '' },
    funder_code: { isValid: null, message: '' }
  });

  // Track form submission and validation errors
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    fetchCountries();
  }, []);

  // Check if form is valid for submission
  const isFormValid = () => {
    // Check required fields
    const requiredFields = [
      formData.country,
      formData.name,
      formData.short_name,
      formData.funder_code,
      formData.domain,
      primaryRepData.first_name,
      primaryRepData.last_name,
      primaryRepData.phone_number,
      primaryRepData.email_address,
      secondaryRepData.first_name,
      secondaryRepData.last_name,
      secondaryRepData.phone_number,
      secondaryRepData.email_address
    ];

    const allFieldsFilled = requiredFields.every(field => field && field.trim() !== '');
    
    // Check validation states
    const validationsPassed = [
      validation.name?.isValid !== false,
      validation.domain?.isValid !== false,
      validation.short_name?.isValid !== false,
      validation.funder_code?.isValid !== false
    ].every(Boolean);

    // Check if there are no validation errors
    const noValidationErrors = Object.keys(validationErrors).length === 0;

    return allFieldsFilled && validationsPassed && noValidationErrors;
  };

  const fetchCountries = async () => {
    try {
      setLoading(true);
      // Using a public API for countries - you might want to use your own endpoint
      const response = await fetch('https://restcountries.com/v3.1/all?fields=name,cca2');
      const data = await response.json();
      const countryList = data
        .map(country => ({
          value: `${country.cca2} - ${country.name.common}`, // Internal value: "ZA - South Africa"
          display: country.name.common // Display name: "South Africa"
        }))
        .sort((a, b) => a.display.localeCompare(b.display));
      setCountries(countryList);
    } catch (error) {
      console.error('Error fetching countries:', error);
      // Fallback list of common countries with Alpha-2 codes
      setCountries([
        { value: 'ZA - South Africa', display: 'South Africa' },
        { value: 'US - United States', display: 'United States' },
        { value: 'GB - United Kingdom', display: 'United Kingdom' },
        { value: 'CA - Canada', display: 'Canada' },
        { value: 'AU - Australia', display: 'Australia' },
        { value: 'DE - Germany', display: 'Germany' },
        { value: 'FR - France', display: 'France' },
        { value: 'NL - Netherlands', display: 'Netherlands' },
        { value: 'SE - Sweden', display: 'Sweden' },
        { value: 'NO - Norway', display: 'Norway' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Clear previous validation
    if (validation[name]) {
      setValidation(prev => ({
        ...prev,
        [name]: { isValid: null, message: '' }
      }));
    }

    // Real-time domain format validation (basic check without API call)
    if (name === 'domain' && value) {
      const hasDot = value.includes('.');
      const basicDomainRegex = /^[a-zA-Z0-9.-]+$/;
      const isBasicFormat = basicDomainRegex.test(value);
      
      if (!hasDot && value.length > 0) {
        setValidation(prev => ({
          ...prev,
          domain: { isValid: false, message: 'Domain must contain at least one dot (e.g., example.com)' }
        }));
      } else if (!isBasicFormat) {
        setValidation(prev => ({
          ...prev,
          domain: { isValid: false, message: 'Domain can only contain letters, numbers, dots, and hyphens' }
        }));
      }
    }

    // Clear messages
    setSuccess('');
  };

  const handlePrimaryRepChange = (e) => {
    const { name, value } = e.target;
    setPrimaryRepData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSecondaryRepChange = (e) => {
    const { name, value } = e.target;
    setSecondaryRepData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleEmailChange = (e, isSecondary = false) => {
    const { value } = e.target;
    const fieldName = isSecondary ? 'secondary_email_address' : 'primary_email_address';
    const otherFieldName = isSecondary ? 'primary_email_address' : 'secondary_email_address';
    
    // Update the email value
    if (isSecondary) {
      handleSecondaryRepChange(e);
    } else {
      handlePrimaryRepChange(e);
    }
    
    // Clear validation errors as user types (to provide immediate feedback when fixing issues)
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        
        // Also clear the "different emails" error from the other field if it exists
        if (newErrors[otherFieldName] && newErrors[otherFieldName].includes('must be different')) {
          delete newErrors[otherFieldName];
        }
        
        return newErrors;
      
      });
    }
  };

  const handleEmailBlur = async (e, isSecondary = false) => {
    const { value } = e.target;
    const fieldName = isSecondary ? 'secondary_email_address' : 'primary_email_address';
    const otherFieldName = isSecondary ? 'primary_email_address' : 'secondary_email_address';
    const otherEmail = isSecondary ? primaryRepData.email_address : secondaryRepData.email_address;
    

    
    if (!value.trim() || !formData.domain) {
      // Clear any existing error for this field if it becomes empty
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
      return;
    }

    const errors = {};
    
    // Check if primary and secondary emails are the same
    if (value && otherEmail && value.toLowerCase() === otherEmail.toLowerCase()) {
      errors[fieldName] = 'Primary and secondary representative emails must be different';
    } else {
      // Check domain validation first
      if (!validateEmailDomain(value, formData.domain)) {

        errors[fieldName] = `Email must use the funder domain: @${formData.domain}`;
      } else {
        // Check if email already exists
        const isEmailValid = await validateEmail(value);
        if (!isEmailValid) {
          errors[fieldName] = 'A user with this email address already exists';
        }
      }
    }

    // Update validation errors - either add new error or clear existing one
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      
      if (Object.keys(errors).length > 0) {
        // Add/update error
        Object.assign(newErrors, errors);
      } else {
        // Clear error for this field if validation passes
        delete newErrors[fieldName];
        
        // Also clear the "different emails" error from the other field if it exists
        if (newErrors[otherFieldName] && newErrors[otherFieldName].includes('must be different')) {
          delete newErrors[otherFieldName];
        }
      }
      
      return newErrors;
    });
  };

  const validateDomain = async (domain) => {
    console.log('🔍 validateDomain called with:', domain);
    
    if (!domain) {
      console.log('❌ Domain validation failed: empty domain');
      return { isValid: false, message: 'Domain is required' };
    }
    
    // Enhanced domain validation
    const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
    const regexTest = domainRegex.test(domain);
    const hasDoubleDots = domain.includes('..');
    const startsWithDot = domain.startsWith('.');
    const endsWithDot = domain.endsWith('.');
    const hasConsecutiveHyphens = domain.includes('--');
    const tooLong = domain.length > 253;
    const hasDot = domain.includes('.');
    
    console.log('🔍 Domain format validation:', {
      domain,
      regexTest,
      hasDoubleDots,
      startsWithDot,
      endsWithDot,
      hasConsecutiveHyphens,
      tooLong,
      hasDot
    });
    
    if (!hasDot) {
      console.log('❌ Domain validation failed: missing dot');
      return { isValid: false, message: 'Domain must contain at least one dot (e.g., example.com)' };
    }
    
    if (tooLong) {
      console.log('❌ Domain validation failed: too long');
      return { isValid: false, message: 'Domain name is too long (maximum 253 characters)' };
    }
    
    if (!regexTest || hasDoubleDots || startsWithDot || endsWithDot || hasConsecutiveHyphens) {
      console.log('❌ Domain validation failed: invalid format');
      return { isValid: false, message: 'Please enter a valid domain format (e.g., example.com, subdomain.example.org)' };
    }

    try {
      console.log('🌐 Making API call to check domain uniqueness:', `accounts/funders/check-domain/?domain=${domain}`);
      const response = await axios.get(`accounts/funders/check-domain/?domain=${domain}`);
      console.log('✅ Domain check API response:', response.data);
      
      if (response.data.exists) {
        console.log('❌ Domain already exists');
        return { isValid: false, message: `Domain "${domain}" is already in use by another funder. Please choose a different domain.` };
      }
      console.log('✅ Domain is available');
      return { isValid: true, message: '' };
    } catch (error) {
      console.error('💥 Error checking domain:', error);
      console.error('💥 Error response:', error.response?.data);
      console.error('💥 Error status:', error.response?.status);
      return { isValid: null, message: 'Unable to verify domain availability. Please try again.' };
    }
  };

  const validateName = async (name) => {
    console.log('🔍 validateName called with:', name);
    
    if (!name) {
      console.log('❌ Name validation failed: empty name');
      return { isValid: false, message: 'Funder name is required' };
    }
    
    try {
      console.log('🌐 Making API call to check name uniqueness:', `accounts/funders/check-name/?name=${encodeURIComponent(name)}`);
      const response = await axios.get(`accounts/funders/check-name/?name=${encodeURIComponent(name)}`);
      console.log('✅ Name check API response:', response.data);
      
      if (response.data.exists) {
        console.log('❌ Name already exists in BMO');
        return { isValid: false, message: `"${name}" already exists in BMO. Please choose a different name.` };
      }
      console.log('✅ Name is available');
      return { isValid: true, message: '' };
    } catch (error) {
      console.error('💥 Error checking name:', error);
      console.error('💥 Error response:', error.response?.data);
      console.error('💥 Error status:', error.response?.status);
      return { isValid: null, message: 'Unable to verify name availability. Please try again.' };
    }
  };

  const validateShortName = async (shortName) => {
    console.log('🔍 validateShortName called with:', shortName);
    
    if (!shortName) {
      console.log('❌ Short name validation failed: empty name');
      return { isValid: false, message: 'Short name is required' };
    }
    
    try {
      console.log('🌐 Making API call to check short name uniqueness:', `accounts/funders/check-short-name/?short_name=${shortName}`);
      const response = await axios.get(`accounts/funders/check-short-name/?short_name=${shortName}`);
      console.log('✅ Short name check API response:', response.data);
      
      if (response.data.exists) {
        console.log('❌ Short name already exists in BMO');
        return { isValid: false, message: `Short name "${shortName}" already exists in your BMO. Please choose a different name.` };
      }
      console.log('✅ Short name is available');
      return { isValid: true, message: '' };
    } catch (error) {
      console.error('💥 Error checking short name:', error);
      console.error('💥 Error response:', error.response?.data);
      console.error('💥 Error status:', error.response?.status);
      return { isValid: null, message: 'Unable to verify short name availability. Please try again.' };
    }
  };

  const validateFunderCode = async (funderCode) => {
    console.log('🔍 validateFunderCode called with:', funderCode);
    
    if (!funderCode) {
      console.log('❌ Funder code validation failed: empty code');
      return { isValid: false, message: 'Funder code is required' };
    }
    
    try {
      console.log('🌐 Making API call to check funder code uniqueness:', `accounts/funders/check-funder-code/?funder_code=${funderCode}`);
      const response = await axios.get(`accounts/funders/check-funder-code/?funder_code=${funderCode}`);
      console.log('✅ Funder code check API response:', response.data);
      
      if (response.data.exists) {
        console.log('❌ Funder code already exists in BMO');
        return { isValid: false, message: `Funder code "${funderCode}" already exists in your BMO. Please choose a different code.` };
      }
      console.log('✅ Funder code is available');
      return { isValid: true, message: '' };
    } catch (error) {
      console.error('💥 Error checking funder code:', error);
      console.error('💥 Error response:', error.response?.data);
      console.error('💥 Error status:', error.response?.status);
      return { isValid: null, message: 'Unable to verify funder code availability. Please try again.' };
    }
  };

  const handleBlur = async (e) => {
    const { name, value } = e.target;
    
    console.log('🎯 handleBlur triggered for field:', name, 'with value:', value);
    
    if (!value.trim()) {
      console.log('⏭️ Skipping validation - empty value');
      // Clear any existing validation for this field
      setValidation(prev => ({
        ...prev,
        [name]: { isValid: true, message: '' }
      }));
      return;
    }

    let validationResult;
    switch (name) {
      case 'name':
        console.log('📛 Starting name validation...');
        validationResult = await validateName(value.trim());
        break;
      case 'domain':
        console.log('🏠 Starting domain validation...');
        validationResult = await validateDomain(value.trim());
        break;
      case 'short_name':
        console.log('📝 Starting short name validation...');
        validationResult = await validateShortName(value.trim());
        break;
      case 'funder_code':
        console.log('🏷️ Starting funder code validation...');
        validationResult = await validateFunderCode(value.trim());
        break;
      default:
        console.log('⚠️ Unknown field type for validation:', name);
        return;
    }

    console.log('📊 Validation result for', name, ':', validationResult);
    
    setValidation(prev => ({
      ...prev,
      [name]: validationResult
    }));
  };

  const validateEmail = async (email) => {
    if (!email) return true;
    
    try {
      const response = await axios.post('accounts/check-email/', {
        email_address: email
      });
      return !response.data.exists;
    } catch (error) {
      console.error('Error checking email:', error);
      return true;
    }
  };

  const validateEmailDomain = (email, funderDomain) => {
    if (!email || !funderDomain) {
      return true;
    }
    
    const emailDomain = email.split('@')[1];
    
    if (!emailDomain) {
      return false;
    }
    
    return emailDomain.toLowerCase() === funderDomain.toLowerCase();
  };

  const handleCompleteSubmit = async (e) => {
    e.preventDefault();
    
    const errors = {};
    
    // Validate funder information
    if (!formData.country) errors.country = 'Country is required';
    if (!formData.name) errors.name = 'Funder name is required';
    if (!formData.short_name) errors.short_name = 'Short name is required';
    if (!formData.funder_code) errors.funder_code = 'Funder code is required';
    if (!formData.domain) errors.domain = 'Domain is required';

    if (validation.domain?.isValid === false) {
      errors.domain = validation.domain.message;
    }
    if (validation.short_name?.isValid === false) {
      errors.short_name = validation.short_name.message;
    }
    if (validation.funder_code?.isValid === false) {
      errors.funder_code = validation.funder_code.message;
    }

    // Validate primary representative information
    if (!primaryRepData.first_name.trim()) {
      errors.primary_first_name = 'Primary representative first name is required';
    }
    if (!primaryRepData.last_name.trim()) {
      errors.primary_last_name = 'Primary representative last name is required';
    }
    if (!primaryRepData.phone_number.trim()) {
      errors.primary_phone_number = 'Primary representative phone number is required';
    }
    if (!primaryRepData.email_address.trim()) {
      errors.primary_email_address = 'Primary representative email is required';
    }

    // Validate secondary representative information
    if (!secondaryRepData.first_name.trim()) {
      errors.secondary_first_name = 'Secondary representative first name is required';
    }
    if (!secondaryRepData.last_name.trim()) {
      errors.secondary_last_name = 'Secondary representative last name is required';
    }
    if (!secondaryRepData.phone_number.trim()) {
      errors.secondary_phone_number = 'Secondary representative phone number is required';
    }
    if (!secondaryRepData.email_address.trim()) {
      errors.secondary_email_address = 'Secondary representative email is required';
    }

    // Validate email addresses
    if (primaryRepData.email_address) {
      const isPrimaryEmailValid = await validateEmail(primaryRepData.email_address);
      if (!isPrimaryEmailValid) {
        errors.primary_email_address = 'A user with this email address already exists';
      } else if (!validateEmailDomain(primaryRepData.email_address, formData.domain)) {
        errors.primary_email_address = `Email must use the funder domain: @${formData.domain}`;
      }
    }

    if (secondaryRepData.email_address) {
      const isSecondaryEmailValid = await validateEmail(secondaryRepData.email_address);
      if (!isSecondaryEmailValid) {
        errors.secondary_email_address = 'A user with this email address already exists';
      } else if (!validateEmailDomain(secondaryRepData.email_address, formData.domain)) {
        errors.secondary_email_address = `Email must use the funder domain: @${formData.domain}`;
      }
    }

    // Validate that primary and secondary emails are different
    if (primaryRepData.email_address && secondaryRepData.email_address && 
        primaryRepData.email_address.toLowerCase() === secondaryRepData.email_address.toLowerCase()) {
      errors.primary_email_address = 'Primary and secondary representative emails must be different';
      errors.secondary_email_address = 'Primary and secondary representative emails must be different';
    }

    setValidationErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      setSubmitLoading(true);
      
      // Step 1: Create the funder
      const funderResponse = await axios.post('accounts/funders/create/', formData);
      console.log('Funder creation response:', funderResponse.data);
      const funderId = funderResponse.data.id;
      
      // Step 2: Create primary representative
      const primaryRepPayload = {
        ...primaryRepData,
        funder_id: funderId
      };
      
      console.log('Sending primary representative data:', primaryRepPayload);
      await axios.post('accounts/create-funder-primary-representative/', primaryRepPayload);

      // Step 3: Create secondary representative
      const secondaryRepPayload = {
        ...secondaryRepData,
        funder_id: funderId
      };
      
      console.log('Sending secondary representative data:', secondaryRepPayload);
      await axios.post('accounts/create-funder-secondary-representative/', secondaryRepPayload);

      setCreationStep('complete');
      setSuccess('Funder and representatives created successfully! Activation emails have been sent.');

      setTimeout(() => {
        navigate('/general-dashboard/funders');
      }, 3000);

    } catch (error) {
      console.error('Error creating funder and representatives:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      
      if (error.response?.data) {
        console.error('Validation errors from server:', error.response.data);
        setValidationErrors(error.response.data);
      } else {
        setValidationErrors({
          general: 'An error occurred while creating the funder and representatives. Please check the console for details.'
        });
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner color="primary" />
        <p className="mt-2">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Create New Funder</h2>
        <Button 
          color="secondary" 
          onClick={() => navigate('/general-dashboard/funders')}
        >
          Back to Funders
        </Button>
      </div>

      {success && (
        <Alert color="success" className="mb-4">
          {success}
        </Alert>
      )}

      {validationErrors.general && (
        <Alert color="danger" className="mb-4">
          {validationErrors.general}
        </Alert>
      )}

      {/* Single Form for Funder and Representatives */}
      {creationStep === 'form' && (
        <Form onSubmit={handleCompleteSubmit}>
        {/* Funder Information Section */}
        <Card className="mb-4">
          <CardBody>
            <CardTitle tag="h4" className="mb-4">Funder Information</CardTitle>
            <Row>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <select
                    className={`form-select ${validationErrors.country ? 'is-invalid' : ''}`}
                    id="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    name="country"
                  >
                    <option value="">Select Country</option>
                    {countries.map(country => (
                      <option key={country.value} value={country.value}>{country.display}</option>
                    ))}
                  </select>
                  <label htmlFor="country">Country *</label>
                  {validationErrors.country && (
                    <div className="invalid-feedback">{validationErrors.country}</div>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className={`form-control ${
                      validationErrors.name || validation.name?.isValid === false 
                        ? 'is-invalid' 
                        : ''
                    }`}
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    name="name"
                    placeholder="Funder Name"
                  />
                  <label htmlFor="name">Funder Name *</label>
                  {validationErrors.name && (
                    <div className="invalid-feedback">{validationErrors.name}</div>
                  )}
                  {validation.name?.isValid === false && validation.name?.message && (
                    <div className="invalid-feedback d-block">{validation.name.message}</div>
                  )}
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className={`form-control ${
                      validationErrors.short_name || validation.short_name?.isValid === false 
                        ? 'is-invalid' 
                        : ''
                    }`}
                    type="text"
                    id="short_name"
                    value={formData.short_name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    name="short_name"
                    placeholder="Short Name"
                  />
                  <label htmlFor="short_name">Short Name *</label>
                  {validationErrors.short_name && (
                    <div className="invalid-feedback">{validationErrors.short_name}</div>
                  )}
                  {validation.short_name?.isValid === false && validation.short_name?.message && (
                    <div className="invalid-feedback d-block">{validation.short_name.message}</div>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className={`form-control ${
                      validationErrors.funder_code || validation.funder_code?.isValid === false 
                        ? 'is-invalid' 
                        : ''
                    }`}
                    type="text"
                    id="funder_code"
                    value={formData.funder_code}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    name="funder_code"
                    placeholder="Funder Code"
                  />
                  <label htmlFor="funder_code">Funder Code *</label>
                  {validationErrors.funder_code && (
                    <div className="invalid-feedback">{validationErrors.funder_code}</div>
                  )}
                  {validation.funder_code?.isValid === false && validation.funder_code?.message && (
                    <div className="invalid-feedback d-block">{validation.funder_code.message}</div>
                  )}
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className={`form-control ${
                      validationErrors.domain || validation.domain?.isValid === false 
                        ? 'is-invalid' 
                        : ''
                    }`}
                    type="text"
                    id="domain"
                    value={formData.domain}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    name="domain"
                    placeholder="example.com"
                  />
                  <label htmlFor="domain">Domain *</label>
                  {validationErrors.domain && (
                    <div className="invalid-feedback">{validationErrors.domain}</div>
                  )}
                  {validation.domain?.isValid === false && validation.domain?.message && (
                    <div className="invalid-feedback d-block">{validation.domain.message}</div>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <select
                    className={`form-select ${validationErrors.default_currency ? 'is-invalid' : ''}`}
                    id="default_currency"
                    value={formData.default_currency}
                    onChange={handleInputChange}
                    name="default_currency"
                  >
                    {CURRENCIES.map((currency) => (
                      <option key={currency.code} value={currency.code}>
                        {currency.code} - {currency.name}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="default_currency">Default Currency *</label>
                  {validationErrors.default_currency && (
                    <div className="invalid-feedback">{validationErrors.default_currency}</div>
                  )}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Primary Representative Section */}
        <Card className="mb-4">
          <CardBody>
            <CardTitle tag="h4" className="mb-4">Primary Representative * (Required)</CardTitle>
            <Row>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className={`form-control ${validationErrors.primary_first_name ? 'is-invalid' : ''}`}
                    type="text"
                    id="primary_first_name"
                    value={primaryRepData.first_name}
                    onChange={handlePrimaryRepChange}
                    name="first_name"
                    placeholder="First Name"
                  />
                  <label htmlFor="primary_first_name">First Name *</label>
                  {validationErrors.primary_first_name && (
                    <div className="invalid-feedback">{validationErrors.primary_first_name}</div>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className={`form-control ${validationErrors.primary_last_name ? 'is-invalid' : ''}`}
                    type="text"
                    id="primary_last_name"
                    value={primaryRepData.last_name}
                    onChange={handlePrimaryRepChange}
                    name="last_name"
                    placeholder="Last Name"
                  />
                  <label htmlFor="primary_last_name">Last Name *</label>
                  {validationErrors.primary_last_name && (
                    <div className="invalid-feedback">{validationErrors.primary_last_name}</div>
                  )}
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className="form-control"
                    type="text"
                    id="primary_title"
                    value={primaryRepData.title}
                    onChange={handlePrimaryRepChange}
                    name="title"
                    placeholder="Title"
                  />
                  <label htmlFor="primary_title">Title</label>
                </div>
              </Col>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className={`form-control ${validationErrors.primary_phone_number ? 'is-invalid' : ''}`}
                    type="text"
                    id="primary_phone_number"
                    value={primaryRepData.phone_number}
                    onChange={handlePrimaryRepChange}
                    name="phone_number"
                    placeholder="Phone Number"
                  />
                  <label htmlFor="primary_phone_number">Phone Number *</label>
                  {validationErrors.primary_phone_number && (
                    <div className="invalid-feedback">{validationErrors.primary_phone_number}</div>
                  )}
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className={`form-control ${validationErrors.primary_email_address ? 'is-invalid' : ''}`}
                    type="email"
                    id="primary_email_address"
                    value={primaryRepData.email_address}
                    onChange={(e) => handleEmailChange(e, false)}
                    onBlur={(e) => handleEmailBlur(e, false)}
                    name="email_address"
                    placeholder="Email Address"
                  />
                  <label htmlFor="primary_email_address">Email Address *</label>
                  {validationErrors.primary_email_address && (
                    <div className="invalid-feedback">{validationErrors.primary_email_address}</div>
                  )}
                  {formData.domain && (
                    <small className="text-muted">
                      Must use funder domain: @{formData.domain}
                    </small>
                  )}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Secondary Representative Section */}
        <Card className="mb-4">
          <CardBody>
            <CardTitle tag="h4" className="mb-4">Secondary Representative * (Required)</CardTitle>
            <Row>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className={`form-control ${validationErrors.secondary_first_name ? 'is-invalid' : ''}`}
                    type="text"
                    id="secondary_first_name"
                    value={secondaryRepData.first_name}
                    onChange={handleSecondaryRepChange}
                    name="first_name"
                    placeholder="First Name"
                  />
                  <label htmlFor="secondary_first_name">First Name *</label>
                  {validationErrors.secondary_first_name && (
                    <div className="invalid-feedback">{validationErrors.secondary_first_name}</div>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className={`form-control ${validationErrors.secondary_last_name ? 'is-invalid' : ''}`}
                    type="text"
                    id="secondary_last_name"
                    value={secondaryRepData.last_name}
                    onChange={handleSecondaryRepChange}
                    name="last_name"
                    placeholder="Last Name"
                  />
                  <label htmlFor="secondary_last_name">Last Name *</label>
                  {validationErrors.secondary_last_name && (
                    <div className="invalid-feedback">{validationErrors.secondary_last_name}</div>
                  )}
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className="form-control"
                    type="text"
                    id="secondary_title"
                    value={secondaryRepData.title}
                    onChange={handleSecondaryRepChange}
                    name="title"
                    placeholder="Title"
                  />
                  <label htmlFor="secondary_title">Title</label>
                </div>
              </Col>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className={`form-control ${validationErrors.secondary_phone_number ? 'is-invalid' : ''}`}
                    type="text"
                    id="secondary_phone_number"
                    value={secondaryRepData.phone_number}
                    onChange={handleSecondaryRepChange}
                    name="phone_number"
                    placeholder="Phone Number"
                  />
                  <label htmlFor="secondary_phone_number">Phone Number *</label>
                  {validationErrors.secondary_phone_number && (
                    <div className="invalid-feedback">{validationErrors.secondary_phone_number}</div>
                  )}
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className={`form-control ${validationErrors.secondary_email_address ? 'is-invalid' : ''}`}
                    type="email"
                    id="secondary_email_address"
                    value={secondaryRepData.email_address}
                    onChange={(e) => handleEmailChange(e, true)}
                    onBlur={(e) => handleEmailBlur(e, true)}
                    name="email_address"
                    placeholder="Email Address"
                  />
                  <label htmlFor="secondary_email_address">Email Address *</label>
                  {validationErrors.secondary_email_address && (
                    <div className="invalid-feedback">{validationErrors.secondary_email_address}</div>
                  )}
                  {formData.domain && (
                    <small className="text-muted">
                      Must use funder domain: @{formData.domain}
                    </small>
                  )}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Submit Button */}
        <div className="text-center">
          <Button 
            type="submit" 
            color="primary" 
            size="lg"
            disabled={submitLoading || !isFormValid()}
          >
            {submitLoading ? <Spinner size="sm" className="me-2" /> : ''}
            {submitLoading ? 'Creating Funder and Representatives...' : 'Create Funder and Representatives'}
          </Button>
          {!isFormValid() && !submitLoading && (
            <div className="text-muted mt-2">
              <small>Please fill all required fields and resolve any validation errors to continue.</small>
            </div>
          )}
        </div>
      </Form>
      )}

      {/* Complete Section */}
      {creationStep === 'complete' && (
        <Card className="mt-4">
          <CardBody className="text-center">
            <CardTitle tag="h4" className="text-success mb-4">Success!</CardTitle>
            <p>The funder and representatives have been created successfully.</p>
            <p>Activation emails have been sent to the representatives.</p>
            <p>Redirecting to Manage Funders...</p>
            <Spinner color="primary" />
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default CreateFunder;
